import type { ReactNode } from 'react'
import cx from 'classnames'
import { Tooltip } from '@/components/Tooltip'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetContentBlock,
  ModalSheetContentIllustration,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetHeaderShell,
  ModalSheetHeaderCloseButton,
  ModalSheetHeader,
  LeftSidePanel,
  LeftSidePanelHeading,
  LeftSidePanelTitle,
  LeftSidePanelDescription,
} from '@/components/ModalSheet'
import { match } from '@/utils/pattern-match-util'
import styles from './RequestScopeChangesDialog.module.css'
import { SearchField } from '@/components/SearchField'
import { byDesign } from '@/infra/design'
import imgWelcomeUrl from '@/assets/misc/img_welcome.svg'
import CreateTeamIllustration from '@/components/SpotIllustration/CreateTeamIllustration'
import type { Id } from '@/infra/id'
import type { RequestScopeChangesState } from './RequestScopeChangesDialogViewModel'

export interface RequestScopeChangesDialogProps {
  showing: boolean
  state: RequestScopeChangesState
  isSubmitPending: boolean
  onClose: () => unknown
  children: ReactNode
}

export function RequestScopeChangesDialog({
  showing,
  state,
  isSubmitPending,
  onClose,
  children,
}: RequestScopeChangesDialogProps) {
  const isCurrentStateSuccessfulOrFailed =
    state === 'Success' || state === 'Failed'

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
      leftSidePanelContent={
        isCurrentStateSuccessfulOrFailed ? undefined : (
          <RequestScopeChangesDialogLeftSidePanelContent
            requestScopeChangesState={state}
          />
        )
      }
    >
      {children}
    </ModalSheet>
  )
}

export interface RequestScopeChangesSelectionStepProps {
  searchText: string
  onSearchTextChange: (value: string) => unknown
  showAddCustomItemEmptyState: boolean
  canCreateCustomItem: boolean
  onAddCustomItem: () => unknown
  totalSelectedInventoryItemsCount: number
  isNextDisabled: boolean
  onNext: () => unknown
  children: ReactNode
  searchFieldTestId?: string
  addButtonTestId?: string
  nextButtonTestId?: string
}

export function RequestScopeChangesSelectionStep({
  searchText,
  onSearchTextChange,
  showAddCustomItemEmptyState,
  canCreateCustomItem,
  onAddCustomItem,
  totalSelectedInventoryItemsCount,
  isNextDisabled,
  onNext,
  children,
  searchFieldTestId,
  addButtonTestId,
  nextButtonTestId,
}: RequestScopeChangesSelectionStepProps) {
  return (
    <>
      <RequestScopeChangeHeading title="Select all items to update">
        <div className={styles.searchContainer}>
          <SearchField
            autoFocus
            stretch
            className={styles.scopeSearch}
            placeholder="Type to search"
            onChange={onSearchTextChange}
            value={searchText}
            testId={searchFieldTestId}
            showClearButton
          />
        </div>
      </RequestScopeChangeHeading>

      <GreyContainer centerContent>
        <ModalSheetContent>
          {showAddCustomItemEmptyState ? (
            <div className={styles.addCustomItemContainerEmpty}>
              <div className={styles.customItemTextEmpty}>
                There are no service items with that name.
              </div>
              <Button
                label="Add custom service"
                variant="text"
                onPress={onAddCustomItem}
                testId={addButtonTestId}
              />
            </div>
          ) : (
            <div className={styles.itemsContainer}>
              {children}
              {canCreateCustomItem && (
                <div className={styles.addCustomItemContainer}>
                  <div className={styles.customItemText}>
                    Not what you’re looking for?
                  </div>
                  <Button
                    label="Add custom service"
                    variant="text"
                    onPress={onAddCustomItem}
                    testId={addButtonTestId}
                  />
                </div>
              )}
            </div>
          )}
        </ModalSheetContent>
      </GreyContainer>

      <ModalSheetFooter>
        <FooterInventoryItemsQuantityInfo
          totalSelectedInventoryItemsCount={totalSelectedInventoryItemsCount}
        />
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Next"
            variant="primary"
            onPress={onNext}
            isDisabled={isNextDisabled}
            testId={nextButtonTestId}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface RequestScopeChangesQuantityStepProps {
  totalSelectedInventoryItemsCount: number
  isNextDisabled: boolean
  onBack: () => unknown
  onNext: () => unknown
  children: ReactNode
  nextButtonTestId?: string
}

export function RequestScopeChangesQuantityStep({
  totalSelectedInventoryItemsCount,
  isNextDisabled,
  onBack,
  onNext,
  children,
  nextButtonTestId,
}: RequestScopeChangesQuantityStepProps) {
  return (
    <>
      <RequestScopeChangeHeading title="Compare and adjust quantities">
        <ItemHeadings isUpdatingQuantity />
      </RequestScopeChangeHeading>

      <GreyContainer>
        <ModalSheetContent>
          <div className={styles.itemsContainer}>{children}</div>
        </ModalSheetContent>
      </GreyContainer>

      <ModalSheetFooter>
        <FooterInventoryItemsQuantityInfo
          totalSelectedInventoryItemsCount={totalSelectedInventoryItemsCount}
        />
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Back" onPress={onBack} />
          <Button
            label="Next"
            variant="primary"
            onPress={onNext}
            isDisabled={isNextDisabled}
            testId={nextButtonTestId}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface RequestScopeChangesReviewInventoryItem {
  versionId: Id
  name: string
  description: string
  newQuantity: number
}

export interface RequestScopeChangesReviewCustomItem {
  key: string
  name: string
  quantity: number
}

export interface RequestScopeChangesReviewStepProps {
  inventoryItems: RequestScopeChangesReviewInventoryItem[]
  customItems: RequestScopeChangesReviewCustomItem[]
  reasonField: ReactNode
  isDisabled: boolean
  isSubmitPending: boolean
  onBack: () => unknown
  onSubmit: () => unknown
  submitButtonTestId?: string
}

export function RequestScopeChangesReviewStep({
  inventoryItems,
  customItems,
  reasonField,
  isDisabled,
  isSubmitPending,
  onBack,
  onSubmit,
  submitButtonTestId,
}: RequestScopeChangesReviewStepProps) {
  return (
    <>
      <RequestScopeChangeHeading title="Review and submit changes">
        <ItemHeadings isUpdatingQuantity={false} />
      </RequestScopeChangeHeading>

      <div className={styles.flexContainer}>
        <ModalSheetContent expandable>
          {inventoryItems.map((item) => (
            <div className={styles.listItem} key={item.versionId}>
              <div className={styles.itemContainer}>
                <span>
                  <strong>{item.name}</strong>
                </span>
                <div className={styles.itemDescription}>{item.description}</div>
              </div>

              <span>
                <strong>{item.newQuantity}</strong>
              </span>
            </div>
          ))}
          {customItems.map((item) => (
            <div className={styles.listItem} key={item.key}>
              <span>
                <strong>{item.name}</strong>
              </span>
              <span>
                <strong>{item.quantity}</strong>
              </span>
            </div>
          ))}
          <div className={styles.listItemSeparator}></div>
          <div className={styles.filler}></div>
          <ModalSheetContentBlock expanded>
            {reasonField}
          </ModalSheetContentBlock>
        </ModalSheetContent>
      </div>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Back" isDisabled={isDisabled} onPress={onBack} />
          <Button
            label="Send Updates"
            variant="primary"
            isDisabled={isDisabled}
            isPending={isSubmitPending}
            onPress={onSubmit}
            testId={submitButtonTestId}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface RequestScopeChangesSuccessStepProps {
  onClose: () => unknown
  titleTestId?: string
}

export function RequestScopeChangesSuccessStep({
  onClose,
  titleTestId,
}: RequestScopeChangesSuccessStepProps) {
  return (
    <>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={imgWelcomeUrl}
            illustration={byDesign(undefined, CreateTeamIllustration)}
          />
          <ModalSheetContentTitle testId={titleTestId}>
            Thank you for your request
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            A Customer Success Representative will review and notify you when
            updates have been approved.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

export interface RequestScopeChangesFailedStepProps {
  error: string
  isDisabled: boolean
  onClose: () => unknown
}

export function RequestScopeChangesFailedStep({
  error,
  isDisabled,
  onClose,
}: RequestScopeChangesFailedStepProps) {
  return (
    <>
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            Unable to request scope changes
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            {error || (
              <p>
                We were unable to request scope changes, it's possible that the
                request currently is not in a state where changes are available,
                or it could be a transient error. Please try again. If the issue
                persists, please contact an administrator.
              </p>
            )}
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Cancel" isDisabled={isDisabled} onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </>
  )
}

interface FooterInventoryItemsQuantityInfoProps {
  totalSelectedInventoryItemsCount: number
}

function FooterInventoryItemsQuantityInfo({
  totalSelectedInventoryItemsCount,
}: FooterInventoryItemsQuantityInfoProps) {
  return (
    <div className={styles.footerInfo}>
      {totalSelectedInventoryItemsCount} selected
    </div>
  )
}

interface RequestScopeChangesDialogLeftSidePanelContentProps {
  requestScopeChangesState: RequestScopeChangesState
}

function RequestScopeChangesDialogLeftSidePanelContent({
  requestScopeChangesState,
}: RequestScopeChangesDialogLeftSidePanelContentProps) {
  return (
    <LeftSidePanel>
      {match(requestScopeChangesState, {
        RequestingChanges: () => (
          <>
            <LeftSidePanelHeading>1 of 3</LeftSidePanelHeading>
            <LeftSidePanelTitle>Select items to update</LeftSidePanelTitle>
            <LeftSidePanelDescription>
              Select all items that need to be either updated or removed
            </LeftSidePanelDescription>
          </>
        ),
        UpdatingQuantity: () => (
          <>
            <LeftSidePanelHeading>2 of 3</LeftSidePanelHeading>
            <LeftSidePanelTitle>Update quantities</LeftSidePanelTitle>
            <LeftSidePanelDescription>
              Compare current item totals and capture new quantities
            </LeftSidePanelDescription>
          </>
        ),
        ReviewingChanges: () => (
          <>
            <LeftSidePanelHeading>3 of 3</LeftSidePanelHeading>
            <LeftSidePanelTitle>Review changes</LeftSidePanelTitle>
            <LeftSidePanelDescription>
              Confirm updates and share why changes are being requested
            </LeftSidePanelDescription>
          </>
        ),
        Success: () => undefined,
        Failed: () => undefined,
      })}
    </LeftSidePanel>
  )
}

interface RequestScopeChangeHeadingProps {
  title: string
  children?: ReactNode
}

function RequestScopeChangeHeading({
  title,
  children,
}: RequestScopeChangeHeadingProps) {
  return (
    <ModalSheetHeaderShell size="auto">
      <div className={styles.headerShell}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <div className={styles.closeButtonContainer}>
            <ModalSheetHeaderCloseButton />
          </div>
        </div>
        {children}
      </div>
    </ModalSheetHeaderShell>
  )
}

interface ItemHeadingsProps {
  isUpdatingQuantity: boolean
}

function ItemHeadings({ isUpdatingQuantity }: ItemHeadingsProps) {
  return (
    <div className={styles.listItem}>
      <span>Service item</span>

      {isUpdatingQuantity && (
        <span className={styles.listItemCurrent}>
          Current &nbsp;
          <Tooltip
            content={'Current quantity for each item on the job.'}
            placement="top"
          >
            <Icon name="info" size={18} color="var(--blue400)" />
          </Tooltip>
        </span>
      )}
      <span
        className={
          isUpdatingQuantity
            ? styles.listItemNewTotalQuantitesState
            : styles.listItemNewTotal
        }
      >
        New total &nbsp;
        <Tooltip
          content={
            'New quantity that will replace the current quantity for each item.'
          }
          placement="top"
        >
          <Icon name="info" size={18} color="var(--blue400)" />
        </Tooltip>
      </span>
    </div>
  )
}

interface GreyContainerProps {
  children: ReactNode
  centerContent?: boolean
}

function GreyContainer({
  children,
  centerContent = false,
}: GreyContainerProps) {
  return (
    <div
      className={cx(
        styles.greyBackround,
        centerContent && styles.centerContent,
      )}
    >
      {children}
    </div>
  )
}
